import React from "react";
import { useTranslation } from "react-i18next";
import { AppLayout } from "../shared/layout";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getNotifications, markNotificationAsRead, markAllNotificationsAsRead } from "@/data/requests/notification-requests";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Bell, Check, CheckCheck, Info, AlertTriangle, AlertCircle, CheckCircle2 } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { useToast } from "@/components/ui/use-toast";
import { cn } from "@/lib/utils";
import { ListLoader } from "@/components/shared/page-loader";

export function NotificationsScreen() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: notificationsResponse, isLoading } = useQuery({
    queryKey: ["notifications"],
    queryFn: () => getNotifications(),
  });

  const notifications = notificationsResponse?.data?.data || [];
  const unreadCount = notificationsResponse?.data?.meta?.unreadCount || 0;

  const markReadMutation = useMutation({
    mutationFn: markNotificationAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  const markAllReadMutation = useMutation({
    mutationFn: markAllNotificationsAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
      toast({
        title: t("common.success"),
        description: t("notifications.allMarkedAsRead"),
      });
    },
  });

  const handleMarkAsRead = (id) => {
    markReadMutation.mutate(id);
  };

  const handleMarkAllAsRead = () => {
    markAllReadMutation.mutate();
  };

  const getIcon = (type) => {
    switch (type) {
      case "warning":
        return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
      case "error":
        return <AlertCircle className="h-5 w-5 text-red-500" />;
      case "success":
        return <CheckCircle2 className="h-5 w-5 text-green-500" />;
      default:
        return <Info className="h-5 w-5 text-blue-500" />;
    }
  };

  if (isLoading) {
    return (
      <AppLayout title={t("notifications.title")} description={t("notifications.loading")}>
        <ListLoader count={5} />
      </AppLayout>
    );
  }

  return (
    <AppLayout
      title={t("notifications.title")}
      description={t("notifications.subtitle")}
      actions={[
        {
          name: t("notifications.markAllAsRead"),
          icon: <CheckCheck className="h-4 w-4" />,
          onClick: handleMarkAllAsRead,
          disabled: unreadCount === 0,
        },
      ]}
    >
      <div className="max-w-3xl mx-auto space-y-4">
        {notifications.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-10 text-center">
              <div className="rounded-full bg-primary/10 p-3 mb-3">
                <Bell className="h-6 w-6 text-primary" />
              </div>
              <h3 className="font-medium text-lg">{t("notifications.noNotifications")}</h3>
              <p className="text-sm text-muted-foreground">{t("notifications.allCaughtUp")}</p>
            </CardContent>
          </Card>
        ) : (
          notifications.map((notification) => (
            <Card 
              key={notification.id} 
              className={cn(
                "transition-colors",
                !notification.isRead ? "bg-primary/5 border-primary/20" : "bg-card"
              )}
            >
              <div className="flex items-start p-4 gap-4">
                <div className="mt-1">
                  {getIcon(notification.type)}
                </div>
                <div className="flex-1 space-y-1">
                  <div className="flex items-center justify-between">
                    <p className={cn("font-medium leading-none", !notification.isRead && "font-semibold")}>
                      {notification.title}
                    </p>
                    <span className="text-xs text-muted-foreground">
                      {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {notification.message}
                  </p>
                </div>
                {!notification.isRead && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0"
                    onClick={() => handleMarkAsRead(notification.id)}
                    title={t("notifications.markAsRead")}
                  >
                    <Check className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </Card>
          ))
        )}
      </div>
    </AppLayout>
  );
}

export default NotificationsScreen;
